import React from 'react';
import { css } from 'styled-components';
import Media from 'react-media';
import FormError from 'components/common/FormError';
import FontStyles from 'constants/font-styles';
import Colors from 'constants/colors';
import MediaBorders from 'constants/media-borders';
import { applyStyles, minResolution } from 'util/helpers';
import icPulldown1x from 'assets/images/common/@1x/ic_form_pulldown.png';
import icPulldown2x from 'assets/images/common/@2x/ic_form_pulldown@2x.png';
import icPulldown3x from 'assets/images/common/@3x/ic_form_pulldown@3x.png';

const Select = ({ input, meta, options, onChange, ...inputProps }) => {
  if (input.value !== 0 && !input.value) {
    const option = options[0];

    input.onChange(option.value ? option.value : option);
  }

  return (
    <select
      {...input}
      onChange={e => {
        input.onChange(e);
        if (onChange) {
          onChange(e);
        }
      }}
      {...inputProps}
      className={meta.error && meta.touched ? 'has-error' : ''}
    >
      {options.map(option =>
        option.value != null ? (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ) : (
          <option value={option} key={option}>
            {option}
          </option>
        )
      )}
    </select>
  );
};

const PCSelectContainer = ({ className, meta, notes, ...input }) => (
  <div className={className}>
    <div className="flex-container select-field-ie">
      <Select meta={meta} {...input} />
      {notes && (
        <ul className="notes">
          {notes.map((note, index) => (
            <li key={index}>{note}</li>
          ))}
        </ul>
      )}
    </div>
    <FormError {...meta} />
  </div>
);

const SPSelectContainer = ({ className, meta, notes, ...input }) => (
  <div className={className}>
    <Select meta={meta} {...input} />
    <FormError {...meta} />
    {notes && (
      <ul className="notes">
        {notes.map((note, index) => (
          <li key={index}>{note}</li>
        ))}
      </ul>
    )}
  </div>
);

const commonStyle = css`
  select {
    max-width: 382px;
    width: 100%;
    height: 40px;
    padding: 0 16px 0 8px;
    background-color: ${Colors.white1};
    background-image: url('${icPulldown1x}');
    background-size: 18px;
    background-position: calc(100% - 4px) 50%;
    color: ${Colors.black1};
    border: 0;
    border-radius: 4px;
    box-shadow: inset 0 0 0 1px ${Colors.gray2};
    outline: none;
    appearance: none;
    cursor: pointer;

    &::placeholder {
      color: ${Colors.gray2};
      opacity: 1;
    }

    &.has-error {
      background-color: ${Colors.orange2};
      box-shadow: inset 0 0 0 4px ${Colors.orange1};
    }

    &:disabled {
      background-color: ${Colors.gray4};
      color: ${Colors.gray2};
      background-image: none;
    }
  }

  .notes {
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      color: ${Colors.black1};
      text-indent: -1.25em;
      padding-left: 1.25em;
    }
  }
    
  select::-ms-expand{
    display:none;
  }
`;

const pcStyle = css`
  select {
    ${FontStyles.text8};
  }

  .flex-container {
    display: flex;
    align-items: center;
  }

  .notes {
    margin-left: 16px;

    li {
      ${FontStyles.text12};
    }
  }
`;

const spStyle = css`
  select {
    ${FontStyles.text6};
  }

  .notes {
    margin-top: 8px;

    li {
      ${FontStyles.text8};
    }
  }
`;

const style2x = minResolution(2)`
  select {
    background-image: url('${icPulldown2x}');
  }
`;

const style3x = minResolution(3)`
  select {
    background-image: url('${icPulldown3x}');
  }
`;

const StyledPCSelectContainer = applyStyles(PCSelectContainer)(
  commonStyle,
  pcStyle,
  style2x
);
const StyledSPSelectContainer = applyStyles(SPSelectContainer)(
  commonStyle,
  spStyle,
  style2x,
  style3x
);

const SelectContainer = ({ input, meta, inputProps }) => (
  <Media
    query={`(min-width: ${MediaBorders.umedium})`}
    children={matches =>
      matches ? (
        <StyledPCSelectContainer input={input} meta={meta} {...inputProps} />
      ) : (
        <StyledSPSelectContainer input={input} meta={meta} {...inputProps} />
      )
    }
  />
);

export default SelectContainer;



// WEBPACK FOOTER //
// ./src/components/common/SelectField/components/SelectContainer.js